export interface SubCategory {
  name: string;
  examples?: string[];
  blockedByDefault?: boolean;
}

export interface ParentCategory {
  name: string;
  description: string;
  icon: string;
  subcategories: SubCategory[];
}

export const CATEGORY_HIERARCHY: ParentCategory[] = [
  {
    name: "Jewish",
    description: "Jewish-focused content including news, learning, entertainment, and music",
    icon: "Globe",
    subcategories: [
      { name: "Jewish News", examples: ["yeshivaworld.com", "mishpacha.com", "thelakewoodscoop.com"] },
      { name: "Jewish Learning & Education", examples: ["torahanytime.com", "sefaria.org"] },
      { name: "Jewish Entertainment", examples: ["gruntig.net", "toveedo.com", "meaningfulminute.org"] },
      { name: "Jewish Music", examples: ["24six.app", "jewishmusicstream.com", "jewkbox.com"] }
    ]
  },
  {
    name: "Shopping",
    description: "Online shopping and retail websites",
    icon: "ShoppingBag",
    subcategories: [
      { name: "General Shopping", examples: ["amazon.com", "walmart.com", "target.com"] },
      { name: "Women's General Shopping", examples: ["zara.com/woman"] },
      { name: "Women's Personal Apparel", blockedByDefault: true }
    ]
  },
  {
    name: "Information & Resources",
    description: "Educational content, reference materials, and informational sites",
    icon: "BookOpen",
    subcategories: [
      { name: "Reference", examples: ["wikipedia.org", "britannica.com", "dictionary.com"] },
      { name: "Education", examples: ["khanacademy.org", "coursera.org", "edx.org"] },
      { name: "Health and Medicine", examples: ["webmd.com", "mayoclinic.org", "cdc.gov"] },
      { name: "Other Religions", examples: ["vatican.va", "islamqa.info", "buddhism.net"] },
      { name: "Blogs", examples: ["medium.com", "mashable.com", "lifehacker.com"] },
      { name: "Food & Drink Information", examples: ["allrecipes.com", "weightwatchers.com", "yelp.com"] }
    ]
  },
  {
    name: "Productivity",
    description: "Tools and services for work and productivity",
    icon: "Settings",
    subcategories: [
      { name: "General Tools", examples: ["office.com", "dropbox.com", "trello.com"] },
      { name: "Digital Content Creation", examples: ["canva.com", "adobe.com", "fotor.com"] },
      { name: "Search Engines", examples: ["google.com", "bing.com", "duckduckgo.com"] },
      { name: "AI Chat", examples: ["chat.openai.com", "character.ai", "perplexity.ai"] },
      { name: "Stock Photo Searching", examples: ["unsplash.com", "shutterstock.com", "pixabay.com"] }
    ]
  },
  {
    name: "Communication",
    description: "Social media, messaging, and email platforms",
    icon: "Users",
    subcategories: [
      { name: "Social Media", examples: ["facebook.com", "instagram.com", "linkedin.com"] },
      { name: "Video and Messaging", examples: ["whatsapp.com", "zoom.us", "meet.google.com"] },
      { name: "Email", examples: ["gmail.com", "outlook.com", "protonmail.com"] }
    ]
  },
  {
    name: "Entertainment",
    description: "Gaming, streaming, news, sports, and lifestyle content",
    icon: "Play",
    subcategories: [
      { name: "Gaming", examples: ["kongregate.com", "miniclip.com", "armorgames.com"], blockedByDefault: true },
      { name: "Movies and TV", examples: ["netflix.com", "hulu.com", "crackle.com"], blockedByDefault: true },
      { name: "Music and Audio Streaming", examples: ["spotify.com", "soundcloud.com", "bandcamp.com"], blockedByDefault: true },
      { name: "Video Streaming Platforms", examples: ["youtube.com", "vimeo.com", "dailymotion.com"], blockedByDefault: true },
      { name: "News (Non-Jewish)", examples: ["nytimes.com", "wsj.com", "dailymail.com"], blockedByDefault: true },
      { name: "Sports", examples: ["espn.com", "nba.com", "cbssports.com"], blockedByDefault: true },
      { name: "Style & Fashion", examples: ["vogue.com", "gq.com", "harpersbazaar.com"], blockedByDefault: true },
      { name: "Celebrity", examples: ["tmz.com", "eonline.com", "usmagazine.com"], blockedByDefault: true }
    ]
  },
  {
    name: "Day-to-Day Utilities",
    description: "Essential services for daily life including travel, delivery, and banking",
    icon: "MapPin",
    subcategories: [
      { name: "Travel & Experiences", examples: ["booking.com", "hertz.com", "airbnb.com", "tripadvisor.com"] },
      { name: "Food & Grocery Delivery", examples: ["ubereats.com", "instacart.com", "doordash.com"] },
      { name: "Banking", examples: ["chase.com", "wellsfargo.com", "bankofamerica.com"] },
      { name: "Government Portals", examples: ["usa.gov", "dmv.ny.gov", "ca.gov"] },
      { name: "Navigation & Ride Sharing", examples: ["waze.com", "uber.com", "lyft.com"] }
    ]
  },
  {
    name: "Sensitive Topics",
    description: "Content that may require additional oversight or restriction",
    icon: "AlertTriangle",
    subcategories: [
      { name: "Gambling", blockedByDefault: true },
      { name: "Drugs, Alcohol & Tobacco", blockedByDefault: true },
      { name: "Piracy & Illegal Downloads", blockedByDefault: true },
      { name: "Defacement", blockedByDefault: true },
      { name: "Violence", blockedByDefault: true }
    ]
  }
];

// Get all subcategory names as a flat array
export const ALL_SUBCATEGORY_NAMES: string[] = CATEGORY_HIERARCHY.flatMap(
  parent => parent.subcategories.map(sub => sub.name)
);

// Get default blocked categories
export const DEFAULT_BLOCKED_CATEGORIES: string[] = CATEGORY_HIERARCHY.flatMap(
  parent => parent.subcategories.filter(sub => sub.blockedByDefault).map(sub => sub.name)
);

// Find parent group for a subcategory name
export const getParentForCategory = (categoryName: string): ParentCategory | undefined => {
  return CATEGORY_HIERARCHY.find(parent =>
    parent.subcategories.some(sub => sub.name === categoryName)
  );
};

// Group categories by parent with counts
export const groupCategoriesByParent = (blockedCategories: string[]) => {
  return CATEGORY_HIERARCHY.map(parent => {
    const blockedCount = parent.subcategories.filter(sub => 
      blockedCategories.includes(sub.name)
    ).length;
    const allowedCount = parent.subcategories.length - blockedCount;
    
    return {
      ...parent,
      blockedCount,
      allowedCount,
      isAllBlocked: blockedCount === parent.subcategories.length,
      isAllAllowed: blockedCount === 0,
      isMixed: blockedCount > 0 && blockedCount < parent.subcategories.length
    };
  });
};

// Get subcategory by name
export const getSubcategoryByName = (name: string): SubCategory | undefined => {
  for (const parent of CATEGORY_HIERARCHY) {
    const sub = parent.subcategories.find(s => s.name === name);
    if (sub) return sub;
  }
  return undefined;
};
